import { createAsyncThunk } from "@reduxjs/toolkit"
import { Subscription } from "@/types"
import { httpGet } from "@/lib/http"
import type { RootState } from "@/store"
import { fetchAllOrders } from "@/store/orders/ordersThunks"

export const fetchAllSubscriptions = createAsyncThunk<Subscription[], void, { rejectValue: string }>(
  "subscriptions/fetchAll",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      let allSubs: any[] = []
      let page = 0
      let hasMore = true

      while (hasMore && page < 50) {
        const res = await httpGet({ url: `admin/subscriptions?page=${page}&size=100`, dispatch })
        if (res && res.data && res.data.subscriptions) {
          const { content, last } = res.data.subscriptions
          if (Array.isArray(content)) {
            allSubs = [...allSubs, ...content]
          }
          hasMore = !last
          page++
        } else {
          hasMore = false
        }
      }

      // Ensure orders are loaded so we can look up plan names
      const { loaded: ordersLoaded } = (getState() as RootState).orders
      if (!ordersLoaded) {
        try {
          await dispatch(fetchAllOrders()).unwrap()
        } catch {
          // orders fetch failed — proceed without plan names
        }
      }

      // Build plan name map from orders (joined by database ID)
      const { orders } = (getState() as RootState).orders
      const planMap = new Map<string, string>()
      for (const o of orders) {
        if (o.selectedPlan?.title && o.id) {
          planMap.set(o.id, o.selectedPlan.title)
        }
      }

      return allSubs.map((s, idx) => ({
        id: s.id || `SUB-${String(idx + 1).padStart(4, '0')}`,
        userEmail: s.userEmail,
        orderId: s.orderId,
        planName: s.paymentOrder?.selectedPlan?.title || s.paymentOrder?.selectedPlan?.name || planMap.get(s.orderId) || '—',
        planId: s.paymentOrder?.selectedPlan?.planId || s.planId || null,
        planTier: s.paymentOrder?.selectedPlan?.tier || null,
        planDuration: s.paymentOrder?.selectedPlan?.duration || null,
        status: s.status,
        startDate: s.startDate,
        endDate: s.endDate,
        autoRenew: s.status === 'ACTIVE',
        renewalOrderId: null,
      })) as Subscription[]
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchUserSubscriptions = createAsyncThunk<Subscription[], string, { rejectValue: string }>(
  "subscriptions/fetchByUser",
  async (email, { rejectWithValue, dispatch }) => {
    try {
      const res = await httpGet({ url: `subscriptions/user?userEmail=${email}`, dispatch })
      const data = Array.isArray(res?.data) ? res.data : []
      return data.map((s: any, idx: number) => ({
        id: s.id || `SUB-U-${String(idx + 1).padStart(3, '0')}`,
        userEmail: s.userEmail,
        orderId: s.orderId,
        planName: s.paymentOrder?.selectedPlan?.title || s.paymentOrder?.selectedPlan?.name || '—',
        planId: s.paymentOrder?.selectedPlan?.planId || s.planId || null,
        planTier: s.paymentOrder?.selectedPlan?.tier || null,
        planDuration: s.paymentOrder?.selectedPlan?.duration || null,
        status: s.status,
        startDate: s.startDate,
        endDate: s.endDate,
        autoRenew: s.status === 'ACTIVE',
        renewalOrderId: null,
      })) as Subscription[]
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchSubscriptionsSummary = createAsyncThunk<any, void, { rejectValue: string }>(
  "subscriptions/fetchSummary",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const res = await httpGet({ url: 'admin/subscriptions/summary', dispatch })
      return res?.data?.subscriptionSummary || null
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  }
)
